import { useCallback, useRef } from 'react';

import { useMainPanelSizes, usePanelCollapse } from '../../store/layoutStore';
import { useCollapsiblePane } from './useCollapsiblePane';
import { useIsObjectEditPageActive } from './useIsObjectEditPageActive';

const CHAT_MIN_WIDTH_PX = 400;
const CHAT_MAX_WIDTH_PX = 1400;
const CHAT_SNAP_THRESHOLD_PX = 300;
const CHAT_CONTENT_MIN_WIDTH_PX = 720;
const DEFAULT_CHAT_SIZE_PERCENT = 25;
const OBJECT_EDIT_COPILOT_MIN_WIDTH_PX = 320;
const OBJECT_EDIT_COPILOT_MAX_WIDTH_PX = 480;
const OBJECT_EDIT_MAIN_MIN_WIDTH_PX = 592;

export function useMainChatLayout() {
  const isObjectEditPageActive = useIsObjectEditPageActive();
  const { isChatCollapsed, setChatCollapsed } = usePanelCollapse();
  const { mainPanelSizes, setMainPanelSizes } = useMainPanelSizes();
  const mainPanelSizesRef = useRef(mainPanelSizes);

  mainPanelSizesRef.current = mainPanelSizes;

  const chatMinWidthPx = isObjectEditPageActive
    ? OBJECT_EDIT_COPILOT_MIN_WIDTH_PX
    : CHAT_MIN_WIDTH_PX;
  const chatMaxWidthPx = isObjectEditPageActive
    ? OBJECT_EDIT_COPILOT_MAX_WIDTH_PX
    : CHAT_MAX_WIDTH_PX;
  const mainMinContentWidthPx = isObjectEditPageActive
    ? OBJECT_EDIT_MAIN_MIN_WIDTH_PX
    : CHAT_CONTENT_MIN_WIDTH_PX;

  const handleResizeEnd = useCallback(
    (chatSize: number, mainSize: number, shouldCollapse: boolean) => {
      setMainPanelSizes(
        mainPanelSizesRef.current.map((panelEntry) =>
          panelEntry.id === 'chat'
            ? {
                ...panelEntry,
                size: shouldCollapse ? panelEntry.size : chatSize,
              }
            : panelEntry.id === 'main'
              ? { ...panelEntry, size: mainSize }
              : panelEntry,
        ),
      );
    },
    [setMainPanelSizes],
  );

  return useCollapsiblePane({
    paneId: 'chat',
    contentId: 'main',
    panePosition: 'end',
    minWidthPx: chatMinWidthPx,
    maxWidthPx: chatMaxWidthPx,
    minContentWidthPx: mainMinContentWidthPx,
    collapsedWidthPx: 0,
    snapThresholdPx: CHAT_SNAP_THRESHOLD_PX,
    collapseOnSnap: false,
    persistedSize:
      mainPanelSizes.find((panelEntry) => panelEntry.id === 'chat')?.size ??
      DEFAULT_CHAT_SIZE_PERCENT,
    isCollapsed: isChatCollapsed,
    onCollapseChange: setChatCollapsed,
    onResizeEnd: handleResizeEnd,
  });
}
